import React, { useMemo } from 'react';

import { ScrollRestoration, useLoaderData } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Line } from 'react-chartjs-2';

import * as Product from './product';
import * as Algolia from './algolia';
import { countFormat, Logo, Footer, PillButton, AppHeader } from './template';
import { priceHistoryOptions } from './page-product';
import { CategoryBreadcrumb } from './category-breadcrumb';

const CLUSTER_ATTRIBUTES = [
  'id',
  'slug',
  'title',
  'photo-filename',
  'prices',
  'markets',
  'market-titles',
  'member-count',
  'members',
  'zuper-categories',
  'collected-at',
];

// Validated categorical palette (dataviz skill default, light-mode) -- fixed
// order, never cycled, so a given market keeps the same color across renders.
const CHART_PALETTE = [
  '#2a78d6', // blue
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
  '#e87ba4', // magenta
  '#eb6834', // orange
];

export async function loader({ params }) {
  const id = params.id;

  let cluster;
  try {
    cluster = await Algolia.products.getObject(`cluster-${id}`, {
      attributesToRetrieve: CLUSTER_ATTRIBUTES,
    });
  } catch (e) {
    throw new Response('Cluster not found', {
      status: 404,
      statusText: 'Not Found',
    });
  }

  const memberIds = (cluster.members || []).map((m) => m.id);
  const memberHistories = {};

  if (memberIds.length > 0) {
    try {
      const { results } = await Algolia.products.getObjects(memberIds, {
        attributesToRetrieve: ['id', 'price-history', 'market-title'],
      });
      results.forEach((r) => {
        if (r) memberHistories[r.id] = r;
      });
    } catch (e) {
      // Price history is a nice-to-have on this page -- don't fail the
      // whole page load if it's temporarily unavailable.
      console.error(e);
    }
  }

  return { id, cluster, memberHistories };
}

function sortMembersByPrice(members) {
  return [...(members || [])].sort((a, b) => {
    const pa = a.prices?.['per-item']?.scalar;
    const pb = b.prices?.['per-item']?.scalar;
    if (pa == null) return 1;
    if (pb == null) return -1;
    return pa - pb;
  });
}

function buildPriceHistoryData(members, memberHistories) {
  const series = (members || [])
    .map((member, index) => {
      const record = memberHistories[member.id];
      const history = record?.['price-history']?.['per-item'];
      if (!history) return null;
      return {
        label:
          record['market-title'] || member['market-title'] || member.market,
        history,
        color: CHART_PALETTE[index % CHART_PALETTE.length],
      };
    })
    .filter(Boolean);

  if (series.length === 0) return null;

  const allDates = new Set();
  series.forEach((s) => Object.keys(s.history).forEach((d) => allDates.add(d)));
  const dates = Array.from(allDates).sort();

  return {
    labels: dates.map(Product.formatPriceHistoryDate),
    datasets: series.map((s) => ({
      label: s.label,
      data: dates.map((d) => (d in s.history ? s.history[d] : null)),
      borderColor: s.color,
      backgroundColor: s.color,
      spanGaps: true,
    })),
  };
}

const clusterPriceHistoryOptions = {
  ...priceHistoryOptions,
  plugins: {
    ...priceHistoryOptions.plugins,
    legend: {
      display: true,
      position: 'bottom',
    },
    tooltip: {
      ...priceHistoryOptions.plugins.tooltip,
      callbacks: {
        label: function (context) {
          return `${context.dataset.label}: ${Product.formatPrice({ price: context.parsed.y })}`;
        },
      },
    },
  },
};

function ClusterPriceHistory({ members, memberHistories }) {
  const data = useMemo(
    () => buildPriceHistoryData(members, memberHistories),
    [members, memberHistories]
  );

  if (!data) {
    return (
      <div className="text-gray-500 text-sm">
        Η εξέλιξη τιμής είναι προσωρινά μη διαθέσιμη.
      </div>
    );
  }

  return <Line options={clusterPriceHistoryOptions} data={data} />;
}

// Rendered as cells of the offers grid (see ClusterPage) so the per-item
// and per-unit prices each get their own column and align vertically
// across rows, however many decimals a per-unit price carries. The grid
// only exists from the sm breakpoint up -- five rigid columns can't fit a
// phone (the collapsed title column made prices overlap the market name),
// so mobile gets the stacked MemberRowMobile layout instead.
function MemberRow({ member, cheapest }) {
  const perItem = member.prices?.['per-item']?.scalar;
  const perUnit = member.prices?.['per-unit'];
  return (
    <>
      <div className="border-b py-3 pr-3 flex items-center">
        <Product.MarketIcon market={member.market} />
      </div>
      <div className="border-b py-3 pr-2 min-w-0 flex items-center">
        <div className="text-sm font-semibold text-gray-800 truncate">
          {member['market-title']}
        </div>
      </div>
      <div
        className={`border-b py-3 pl-3 flex items-center justify-end tabular-nums whitespace-nowrap ${cheapest ? 'font-bold' : ''}`}
      >
        {perItem != null && Product.formatPrice({ price: perItem })}
      </div>
      <div className="border-b py-3 pl-3 flex items-center justify-end text-xs text-gray-500 tabular-nums whitespace-nowrap">
        {perUnit?.scalar > 0 &&
          `${Product.formatPrice({ price: perUnit.scalar })}/${Product.formatUnit(perUnit.unit)}`}
      </div>
      <div className="border-b py-3 pl-3 flex items-center justify-end">
        <a
          href={Product.marketRedirectURL(member)}
          target="_blank"
          rel="noreferrer"
        >
          <PillButton variant="grey">Κατάστημα</PillButton>
        </a>
      </div>
    </>
  );
}

function MemberRowMobile({ member, cheapest }) {
  const perItem = member.prices?.['per-item']?.scalar;
  const perUnit = member.prices?.['per-unit'];
  return (
    <div className="border-b py-3 flex items-center gap-3">
      <div className="flex-none">
        <Product.MarketIcon market={member.market} />
      </div>
      <div className="min-w-0 flex-grow">
        <div className="text-sm font-semibold text-gray-800 truncate">
          {member['market-title']}
        </div>
        {perUnit?.scalar > 0 && (
          <div className="text-xs text-gray-500 tabular-nums">
            {Product.formatPrice({ price: perUnit.scalar })}/
            {Product.formatUnit(perUnit.unit)}
          </div>
        )}
      </div>
      <div className="flex-none flex items-center gap-3">
        <div
          className={`tabular-nums whitespace-nowrap ${cheapest ? 'font-bold' : ''}`}
        >
          {perItem != null && Product.formatPrice({ price: perItem })}
        </div>
        <a
          href={Product.marketRedirectURL(member)}
          target="_blank"
          rel="noreferrer"
        >
          <PillButton variant="grey">Κατάστημα</PillButton>
        </a>
      </div>
    </div>
  );
}

export function ClusterPage() {
  const { cluster, memberHistories } = useLoaderData();

  const sortedMembers = useMemo(
    () => sortMembersByPrice(cluster.members),
    [cluster.members]
  );
  const memberCount = cluster['member-count'] || sortedMembers.length;

  // Lowest per-item price across members -- every row carrying it (ties
  // included) renders its price bold. sortMembersByPrice puts nulls last,
  // so the first member's price IS the minimum when it exists.
  const cheapestPrice = sortedMembers[0]?.prices?.['per-item']?.scalar;
  const isCheapest = (member) =>
    cheapestPrice != null &&
    member.prices?.['per-item']?.scalar === cheapestPrice;

  const canonical = `https://www.zuper-market.gr/cluster/${String(cluster.id).replace(/^cluster-/, '')}/${cluster.slug}`;

  // One entity, many sellers: AggregateOffer is Google's documented shape
  // for "a product that is being sold by multiple merchants" -- exactly
  // what this page visibly renders (member prices sorted, cheapest
  // first). The per-market product pages keep their singular Offer
  // (price-drop eligibility requires Offer, and each shows ONE market's
  // listing); this block is additive, not a replacement. Same
  // all-or-nothing discipline as page-product: emit only with a real
  // image and a usable price spread -- a partial block would trade rich
  // results for Search Console errors, and the no-photo placeholder must
  // never appear in structured data (Google requires the image to depict
  // the product).
  const memberPrices = sortedMembers
    .map((m) => m.prices?.['per-item']?.scalar)
    .filter((p) => p > 0);
  const clusterPhoto = cluster['photo-filename']
    ? Product.photoURL(cluster)
    : undefined;
  const clusterJsonLd =
    clusterPhoto && memberPrices.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: cluster.title,
          sku: String(cluster.id),
          image: clusterPhoto,
          offers: {
            '@type': 'AggregateOffer',
            url: canonical,
            priceCurrency: 'EUR',
            lowPrice: String(Math.min(...memberPrices)),
            highPrice: String(Math.max(...memberPrices)),
            offerCount: memberPrices.length,
          },
        }
      : undefined;

  return (
    <div>
      <Helmet>
        <title>{`${cluster.title} — σύγκριση τιμών σε ${countFormat.format(memberCount)} σούπερ μάρκετ | zuper`}</title>
        <meta
          name="description"
          content={`Σύγκρινε τιμές για ${cluster.title} σε ${countFormat.format(memberCount)} σούπερ μάρκετ και βρες πού είναι πιο φθηνό.`}
        />
        <link rel="canonical" href={canonical} />
        {clusterPhoto && <meta property="og:image" content={clusterPhoto} />}
        {clusterJsonLd && (
          <script type="application/ld+json">
            {JSON.stringify(clusterJsonLd)}
          </script>
        )}
      </Helmet>

      <div className="pb-4 justify-center flex lg:flex hidden">
        <Logo size="4xl" />
      </div>

      <AppHeader />

      <div className="max-w-3xl mx-auto p-8">
        <CategoryBreadcrumb categories={cluster['zuper-categories']} />

        <h2 className="text-xl font-semibold">{cluster.title}</h2>

        <p className="text-sm text-gray-600 pt-1">
          Πόσο κάνει το {cluster.title}; Δες παρακάτω τη φθηνότερη τιμή και πώς
          κατανέμεται σε {countFormat.format(memberCount)} σούπερ μάρκετ.
        </p>

        <div className="min-h-full">
          <div className="flex py-4">
            <div className="flex-none m-2">
              <img
                className="object-cover w-36 h-36"
                src={Product.photoURL(cluster)}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = Product.noPhoto;
                }}
              />
            </div>
            <div className="flex-grow pl-4">
              <div className="pb-2">
                <Product.MarketIconsRow
                  markets={cluster.markets}
                  max={cluster.markets?.length || 0}
                />
              </div>
              <div className="pt-2 pb-2">
                <Product.PriceTag
                  product={cluster}
                  showFromPrefix={memberCount > 1}
                />
              </div>
              {cluster['collected-at'] && (
                <div className="text-gray-500 text-sm pb-4">
                  Οι τιμές ανανεώθηκαν πριν{' '}
                  {Product.formatDuration(
                    (Date.now() - cluster['collected-at']) / 1000
                  )}
                </div>
              )}
            </div>
          </div>

          <h3 className="text-lg py-4">Τιμές ανά σούπερ μάρκετ</h3>

          {sortedMembers.length > 0 ? (
            <>
              <div className="hidden sm:grid grid-cols-[auto_minmax(0,1fr)_auto_auto_auto] [&>*:nth-last-child(-n+5)]:border-b-0">
                {sortedMembers.map((member) => (
                  <MemberRow
                    key={member.id}
                    member={member}
                    cheapest={isCheapest(member)}
                  />
                ))}
              </div>
              <div className="sm:hidden [&>*:last-child]:border-b-0">
                {sortedMembers.map((member) => (
                  <MemberRowMobile
                    key={member.id}
                    member={member}
                    cheapest={isCheapest(member)}
                  />
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              Δεν υπάρχουν διαθέσιμες τιμές αυτή τη στιγμή.
            </p>
          )}

          <h3 className="text-lg py-4">Εξέλιξη τιμής</h3>
          <ClusterPriceHistory
            members={cluster.members}
            memberHistories={memberHistories}
          />
        </div>

        <div className="pt-32">
          <Footer />
        </div>
        <ScrollRestoration />
      </div>
    </div>
  );
}
